import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getNewUserDataReportList } from 'app/api/report';
import { startExportPoll, triggerDownload } from 'app/utils/exportPoller';
import Message from 'app/utils/Message';
import Toast from 'app/utils/Toast';

const useNewUserReport = () => {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const navigate = useNavigate();
  const [values, setValues] = useState({
    referrerContact: '',
    registrationDateStart: '',
    registrationDateEnd: '',
  });
  const [totalNumber, setTotalNumber] = useState(0);
  const [filteredData, setFilteredData] = useState([]);
  const [pageLoading, setPageLoading] = useState(false);
  const [pageCount, setPageCount] = useState(1);
  const currentPage = 1;
  const dataPerPage = 10;
  const [isFiltering, setIsFiltering] = useState(false);
  const downloadInProgressRef = useRef(false);
  const [downloadInProgress, setDownloadInProgress] = useState(false);

  const sectionObj = {
    sectionTitle: 'New User Data Report',
    searchObj: {
      keys: ['invoice_number', 'shopping_amount', 'storeName', 'phoneNumber'],
      placeholder: 'Search  Name, Contact No.',
      searchType: 'server',
      handleSearch: (e) => {
        console.log('Handle search: ', e);
      },
    },
    buttons: [
      {
        customclassName: 'bg-white margin-right-class',
        imgTrue: false,
        title: 'Filter',
        handleBtnClick: () => setIsFilterOpen(!isFilterOpen),
      },
    ],
    downloadObj: {
      excelClick: (e) => {
        handleDownloadexcel(e);
      },
      csvClick: (e) => {
        handleDownloadcsv(e);
      },

      pdfClick: () => () => {},
      inProgress: downloadInProgress,
    },
  };

  const fetchReportData = useCallback(
    async (page, search = '', filters = {}) => {
      setPageLoading(true);
      try {
        let requestParams = {
          page,
          limit: dataPerPage,
          search,
          referrerContact: filters.referrerContact,
          registrationDateStart: filters.registrationDateStart,
          registrationDateEnd: filters.registrationDateEnd,
        };

        const response = await getNewUserDataReportList(requestParams);

        if (response?.responseCode === 200) {
          const dataArr = response?.data?.data || [];
          setFilteredData(dataArr);

          const totalCount = response?.data?.count || 0;
          const tempcount = Math.ceil(totalCount / dataPerPage);
          setTotalNumber(totalCount);
          setPageCount(tempcount);
        } else {
          Toast.error(
            response?.response?.message || Message.Error.COMMON_MESSAGE
          );
        }
      } catch (error) {
        Toast.error(Message.Error.COMMON_MESSAGE);
      } finally {
        setPageLoading(false);
      }
    },
    [dataPerPage]
  );
  useEffect(() => {
    if (isFiltering) {
      handleFiltering();
      setIsFiltering(false); // Reset filtering flag after filtering is completed
    }
  }, [isFiltering, filteredData, values, filteredData]);

  function handleFiltering() {
    const filtered = filteredData?.filter((item) => {
      let isReferrerContactMatching =
        !values.referrerContact ||
        item?.referrerContact
          ?.toLowerCase()
          .includes(values.referrerContact.toLowerCase());

      let isRegistrationDateStartMatching =
        !values.registrationDateStart ||
        new Date(item?.registrationDate) >=
          new Date(values.registrationDateStart);

      let isRegistrationDateEndMatching =
        !values.registrationDateEnd ||
        new Date(item?.registrationDate) <=
          new Date(values.registrationDateEnd);

      return (
        isReferrerContactMatching &&
        isRegistrationDateStartMatching &&
        isRegistrationDateEndMatching
      );
    });

    setFilteredData(filtered);
  }
  function handleSubmit(event) {
    event.preventDefault();
    setIsFiltering(true);
  }
  function resetfilterModel() {
    setValues({
      referrerContact: '',
      registrationDateStart: '',
      registrationDateEnd: '',
    });
    setFilteredData([]);
    setIsFiltering(true);
  }
  function handleFilterCancel() {
    setIsFilterOpen(false);
  }
  // Async export: returns a jobId, then polls /export/status/:jobId. We DON'T
  // block the UI with pageLoading — the Toast is enough feedback, and forcing
  // a full-page loader prevents the user from working while the export runs
  // (which can take minutes for large datasets).
  async function startDownload(downloadType) {
    if (downloadInProgressRef.current) return;
    downloadInProgressRef.current = true;
    setDownloadInProgress(true);
    const requestParams = {
      page: 1,
      limit: totalNumber,
      search: '',
      mallName: values.mallName,
      referrerContact: values.referrerContact,
      registrationDateStart: values.registrationDateStart,
      registrationDateEnd: values.registrationDateEnd,
      download: true,
      downloadType,
    };

    try {
      const response = await getNewUserDataReportList(requestParams);
      const jobId = response?.data?.jobId;
      if (response?.responseCode !== 200 || !jobId) {
        Toast.error(response?.response?.message || Message.Error.COMMON_MESSAGE);
        downloadInProgressRef.current = false;
        setDownloadInProgress(false);
        return;
      }

      Toast.success('Report generation started');

      startExportPoll(jobId, {
        onDone: (url) => {
          triggerDownload(url);
          downloadInProgressRef.current = false;
          setDownloadInProgress(false);
        },
        onFailed: (error) => {
          Toast.error(error || Message.Error.COMMON_MESSAGE);
          downloadInProgressRef.current = false;
          setDownloadInProgress(false);
        },
        onError: () => {
          Toast.error(Message.Error.COMMON_MESSAGE);
          downloadInProgressRef.current = false;
          setDownloadInProgress(false);
        },
      });
    } catch (error) {
      console.error('startDownload failed:', error);
      Toast.error(Message.Error.COMMON_MESSAGE);
      downloadInProgressRef.current = false;
      setDownloadInProgress(false);
    }
  }

  function handleDownloadcsv() {
    return startDownload('csv');
  }

  function handleDownloadexcel() {
    return startDownload('excel');
  }

  return {
    pageLoading,
    filteredData,
    navigate,
    totalNumber,
    currentPage,
    dataPerPage,
    pageCount,
    sectionObj,
    isFilterOpen,
    setIsFilterOpen,
    values,
    setValues,
    resetfilterModel,
    handleSubmit,
    handleFilterCancel,
    fetchReportData,
    isFiltering,
  };
};

export default useNewUserReport;
